import { useCallback, useEffect, useMemo, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Card,
  Divider,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import TaskIcon from "@mui/icons-material/TaskAltOutlined"
import ViewKanbanIcon from "@mui/icons-material/ViewKanbanOutlined"
import ViewListIcon from "@mui/icons-material/ViewListOutlined"
import PersonIcon from "@mui/icons-material/PersonOutline"
import OutboxIcon from "@mui/icons-material/OutboxOutlined"
import PersonOffIcon from "@mui/icons-material/PersonOffOutlined"
import OverdueIcon from "@mui/icons-material/ErrorOutline"
import OpenIcon from "@mui/icons-material/RadioButtonUncheckedOutlined"

import { taskApi, clientApi, projectApi, userApi } from "../api"
import { usePermissions } from "../permissions"
import PageHeader from "../components/PageHeader"
import EmptyState from "../components/EmptyState"
import { LoadingRows } from "../components/LoadingState"
import { titleCase } from "../utils/format"
import ClientSelect from "../components/ClientSelect"
import TaskBoard from "./tasks/TaskBoard"
import TaskList from "./tasks/TaskList"
import TaskDialog from "./tasks/TaskDialog"
import TaskDetailDrawer from "./tasks/TaskDetailDrawer"
import ProjectSelect from "../components/ProjectSelect"
import PersonSelect from "../components/PersonSelect"
import QuickFilters from "../components/QuickFilters"
import { useUndo } from "../context/UndoContext"
import SavedViews from "../components/SavedViews"

/* `open: true` is the default view, not a filter somebody applied — Clear
   filters returns here rather than to an empty object, or "clear" would quietly
   widen the list to every closed task ever raised. */
const DEFAULT_FILTERS = {
  client_id: "",
  project_id: "",
  assignee_id: "",
  status_id: "",
  priority: "",
  type: "",
  q: "",
  mine: false,
  reported_by_me: false,
  unassigned: false,
  overdue: false,
  open: true,
}

/*
 * The questions people arrive holding, in the order they ask them.
 *
 * "Assigned to me" and "Reported by me" are different lists and both matter:
 * the first is what you owe, the second is what you asked for and heard nothing
 * about. "Unassigned" is the queue that grows when nobody is looking, and
 * "Overdue" is the one that matters most and is hardest to find by scrolling.
 */
/* The order the server sorts by, so the list reads the same way the filter
   does. Their labels are title-cased here rather than stored capitalised,
   because the value is what goes on the wire. */
const PRIORITIES = ["urgent", "high", "medium", "low"]

const TASK_VIEWS = [
  { key: "mine", label: "Assigned to me", icon: PersonIcon },
  { key: "reported_by_me", label: "Reported by me", icon: OutboxIcon },
  { key: "unassigned", label: "Unassigned", icon: PersonOffIcon },
  { key: "overdue", label: "Overdue", icon: OverdueIcon },
  { key: "open", label: "Open only", icon: OpenIcon },
]

export default function TasksPage() {
  const { can, isBackOffice } = usePermissions()

  const [view, setView] = useState("board")
  const [tasks, setTasks] = useState([])
  const [statuses, setStatuses] = useState([])
  const [clients, setClients] = useState([])
  const [projects, setProjects] = useState([])
  const [people, setPeople] = useState([])
  const { offer } = useUndo()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [openTaskId, setOpenTaskId] = useState(null)

  const [filters, setFilters] = useState(DEFAULT_FILTERS)

  /*
   * The directory is served a page at a time, so the picker asks for more as
   * somebody types rather than filtering the page it happens to hold.
   *
   * This is also what loads the first page: the picker calls it once on mount
   * with an empty query, so a separate effect doing the same fetch would only
   * be a second identical request. `useCallback` because SearchSelect debounces
   * on this function's identity, and a new one each render restarts the timer
   * forever.
   *
   * `/users/directory` rather than `/users`: every signed-in account can read
   * it, where listing users needs a permission a client member does not have —
   * and they still need to filter their own team's work by who is on it.
   */
  const searchPeople = useCallback((query) => {
    userApi
      .directory(query || undefined)
      .then((data) => setPeople(data.users || []))
      .catch(() => {})
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = { limit: 200 }
      if (filters.client_id) params.client_id = filters.client_id
      if (filters.project_id) params.project_id = filters.project_id
      if (filters.assignee_id) params.assignee_id = filters.assignee_id
      if (filters.status_id) params.status_id = filters.status_id
      if (filters.priority) params.priority = filters.priority
      if (filters.type) params.type = filters.type
      if (filters.q.trim()) params.q = filters.q.trim()
      if (filters.mine) params.mine = "true"
      if (filters.reported_by_me) params.reported_by_me = "true"
      if (filters.unassigned) params.unassigned = "true"
      if (filters.overdue) params.overdue = "true"
      if (filters.open) params.open = "true"

      const data = await taskApi.list(params)
      setTasks(data.tasks || [])
    } catch (err) {
      setError(err.userMessage)
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    // Debounced only while typing: a click on a chip should not wait 350ms.
    const timer = setTimeout(load, filters.q ? 350 : 0)
    return () => clearTimeout(timer)
  }, [load, filters.q])

  useEffect(() => {
    taskApi
      .statuses()
      .then((data) => setStatuses(data.statuses || []))
      .catch(() => setStatuses([]))

    projectApi
      .list({ limit: 200 })
      .then((data) => setProjects(data.projects || []))
      .catch(() => setProjects([]))

    if (isBackOffice && can("clients", "view")) {
      clientApi
        .list()
        .then((data) => setClients(data.organizations || []))
        .catch(() => setClients([]))
    }
  }, [isBackOffice, can])

  /**
   * Moving a card.
   *
   * Optimistic: the card moves, then the request goes. A board that waits for a
   * round trip before the card lands feels broken on a slow connection, and the
   * failure case — putting it back and saying why — is rare and cheap.
   */
  const moveTask = useCallback(
    async (taskId, statusId, { announce = true } = {}) => {
      const previous = tasks
      const wasIn = previous.find((t) => Number(t.id) === Number(taskId))?.status_id
      const status = statuses.find((s) => s.id === Number(statusId))
      setTasks((current) =>
        current.map((t) =>
          Number(t.id) === Number(taskId)
            ? { ...t, status_id: Number(statusId), status_name: status?.name, status_label: status?.label, status_color: status?.color, is_terminal: status?.is_terminal }
            : t,
        ),
      )

      try {
        await taskApi.update(taskId, { status_id: Number(statusId) })
        /*
         * Undo rather than "are you sure".
         *
         * A drag is easy to do by accident and there is no confirming it
         * without taxing every deliberate one. `announce` stops the undo
         * itself offering an undo, which would be a loop somebody could ride
         * back and forth forever.
         */
        if (announce && wasIn && Number(wasIn) !== Number(statusId)) {
          const back = statuses.find((s) => s.id === Number(wasIn))
          offer(`Moved to ${status?.label || "a new column"}.`, () =>
            moveTask(taskId, wasIn, { announce: false }).then(() => {
              setError(null)
              return `Moved back to ${back?.label || "where it was"}.`
            }),
          )
        }
      } catch (err) {
        setTasks(previous)
        setError(err.userMessage)
      }
    },
    [tasks, statuses, offer],
  )

  const visibleProjects = useMemo(
    () => (filters.client_id ? projects.filter((p) => String(p.client_id) === String(filters.client_id)) : projects),
    [projects, filters.client_id],
  )

  const filtersActive = Object.keys(DEFAULT_FILTERS).some((key) => filters[key] !== DEFAULT_FILTERS[key])

  const setFilter = (key) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value
    setFilters((current) => ({
      ...current,
      [key]: value,
      // A project from the previous client would return nothing and look like a bug.
      ...(key === "client_id" ? { project_id: "" } : {}),
    }))
  }

  return (
    <Box>
      <PageHeader
        title="Tasks & Issues"
        subtitle={isBackOffice ? "Work across every client, in one place." : "Your team's work."}
        actions={
          <Stack direction="row" spacing={1}>
            <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, value) => value && setView(value)}>
              <ToggleButton value="board" aria-label="Board">
                <ViewKanbanIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="list" aria-label="List">
                <ViewListIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
            {can("tasks", "create") ? (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
                New task
              </Button>
            ) : null}
          </Stack>
        }
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      <Card sx={{ p: "12px 14px", mb: 2 }}>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
          {isBackOffice && clients.length > 0 ? (
            <ClientSelect
              clients={clients}
              value={filters.client_id}
              onChange={(id) =>
                // A project from the previous client would return nothing and look like a bug.
                setFilters((current) => ({ ...current, client_id: id, project_id: "" }))
              }
              emptyLabel="All clients"
              sx={{ minWidth: 190 }}
            />
          ) : null}

          <ProjectSelect
            projects={visibleProjects}
            value={filters.project_id}
            onChange={(id) => setFilters((current) => ({ ...current, project_id: id }))}
            sx={{ minWidth: 190 }}
          />

          {/* Somebody else's list, which is the question a lead asks. "Assigned
              to me" below is the same filter pointed at yourself, kept separate
              because it is one click and this is three. */}
          <PersonSelect
            people={people}
            onSearch={searchPeople}
            label="Assignee"
            value={filters.assignee_id}
            onChange={(id) => setFilters((current) => ({ ...current, assignee_id: id, unassigned: false, mine: false }))}
            emptyLabel="Anyone"
            sx={{ minWidth: 180 }}
          />

          <TextField select size="small" label="Type" value={filters.type} onChange={setFilter("type")} sx={{ minWidth: 130 }} slotProps={{ select: { displayEmpty: true } }}>
            <MenuItem value="">All types</MenuItem>
            <MenuItem value="task">Task</MenuItem>
            <MenuItem value="issue">Issue</MenuItem>
            <MenuItem value="bug">Bug</MenuItem>
            <MenuItem value="request">Request</MenuItem>
          </TextField>

          {/* Short fixed lists, so selects rather than combo boxes: there is
              nothing to type that reading the four options would not answer
              faster. The statuses come from the server because an administrator
              can add one. */}
          <TextField select size="small" label="Status" value={filters.status_id} onChange={setFilter("status_id")} sx={{ minWidth: 140 }} slotProps={{ select: { displayEmpty: true } }}>
            <MenuItem value="">Any status</MenuItem>
            {statuses.map((status) => (
              <MenuItem key={status.id} value={status.id}>
                {status.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField select size="small" label="Priority" value={filters.priority} onChange={setFilter("priority")} sx={{ minWidth: 130 }} slotProps={{ select: { displayEmpty: true } }}>
            <MenuItem value="">Any priority</MenuItem>
            {PRIORITIES.map((priority) => (
              <MenuItem key={priority} value={priority}>
                {titleCase(priority)}
              </MenuItem>
            ))}
          </TextField>

          {/*
            * A search box, at last.
            *
            * The server has matched on title and task code all along; nothing
            * on this page ever sent it. With two hundred tasks the only way to
            * find one by name was to read the board.
            */}
          <TextField
            size="small"
            label="Search"
            value={filters.q}
            onChange={setFilter("q")}
            placeholder="Title or code"
            sx={{ minWidth: 200 }}
          />

        </Stack>

        {/* A rule between the two rows: above narrows the list by what a task
            belongs to, below by where it stands. Without the line they read as
            one long row of unrelated controls that happens to wrap. */}
        <Divider sx={{ my: 1.25 }} />

        {/*
          * Views, state and the count live on the bottom row with the toggles,
          * not trailing the selects.
          *
          * With seven controls the top row wraps, and anything after a flex
          * spacer wraps with it — which stranded "Clear filters · 25 tasks"
          * under the first field, reading as a caption belonging to it. Down
          * here they hold one position whatever the width.
          */}
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
          <QuickFilters
            items={TASK_VIEWS}
            values={filters}
            onToggle={(key) =>
              setFilters((current) => ({
                ...current,
                [key]: !current[key],
                /* Two of these contradict the Assignee box above. Leaving it
                   set would return nothing and read as a broken filter rather
                   than as two questions that cannot both be answered. */
                ...(key === "mine" || key === "unassigned" ? { assignee_id: "" } : {}),
                ...(key === "mine" && !current.mine ? { unassigned: false } : {}),
                ...(key === "unassigned" && !current.unassigned ? { mine: false } : {}),
              }))
            }
          />

          <Box sx={{ flex: 1 }} />

          {/* The combination, kept. Five controls rebuilt every morning is a
              question people stop asking. */}
          <SavedViews
            page="tasks"
            filters={filters}
            defaults={DEFAULT_FILTERS}
            /* Spread over the defaults rather than replacing outright: a view
               saved before this page gained a control would otherwise leave
               that control undefined, and an uncontrolled MUI field warns and
               then stops accepting input. */
            onApply={(saved) => setFilters({ ...DEFAULT_FILTERS, ...saved })}
          />

          {/* Only when there is something to clear. A permanent Clear filters
              button is a control that does nothing most of the time, and one
              that does nothing is one more thing to read past. */}
          {filtersActive ? (
            <Button size="small" onClick={() => setFilters(DEFAULT_FILTERS)}>
              Clear filters
            </Button>
          ) : null}
          <Typography variant="caption" color="text.secondary">
            {tasks.length} task{tasks.length === 1 ? "" : "s"}
          </Typography>
        </Stack>
      </Card>

      {loading ? (
        <LoadingRows rows={6} height={64} />
      ) : tasks.length === 0 ? (
        <Card>
          <EmptyState
            icon={TaskIcon}
            title="Nothing here"
            description={
              filters.mine || filters.client_id || filters.project_id || filters.type
                ? "No tasks match these filters. Try clearing one."
                : "No tasks yet. Raise the first one."
            }
            actionLabel={can("tasks", "create") ? "New task" : null}
            onAction={() => setCreateOpen(true)}
          />
        </Card>
      ) : view === "board" ? (
        <TaskBoard tasks={tasks} statuses={statuses} onMove={moveTask} onOpen={setOpenTaskId} canMove={can("tasks", "update")} />
      ) : (
        <TaskList tasks={tasks} onOpen={setOpenTaskId} />
      )}

      <TaskDialog
        open={createOpen}
        statuses={statuses}
        projects={projects}
        clients={clients}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          setCreateOpen(false)
          load()
        }}
      />

      <TaskDetailDrawer
        taskId={openTaskId}
        statuses={statuses}
        onClose={() => setOpenTaskId(null)}
        onChanged={load}
      />
    </Box>
  )
}
